import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Eye, EyeOff, Landmark, ShieldAlert, KeyRound, Copy, CheckCircle2 } from "lucide-react";

export default function ChefBanques() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [revealId, setRevealId] = useState(null);
  const [revealPwd, setRevealPwd] = useState("");
  const [revealing, setRevealing] = useState(false);
  const [revealedData, setRevealedData] = useState(null);

  function empty() {
    return { nom_banque: "", titulaire: "", iban: "", bic: "", note: "" };
  }

  const load = async () => {
    try {
      const { data } = await api.get("/chef/banks");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/chef/banks/${editing.id}`, form);
        toast.success("Compte mis à jour");
      } else {
        await api.post("/chef/banks", form);
        toast.success("Compte ajouté");
      }
      setOpen(false); setEditing(null); setForm(empty());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ nom_banque: b.nom_banque || "", titulaire: b.titulaire || "", iban: "", bic: b.bic || "", note: b.note || "" }); setOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce compte bancaire ?")) return;
    try { await api.delete(`/chef/banks/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startReveal = (id) => { setRevealId(id); setRevealPwd(""); setRevealedData(null); };

  const submitReveal = async (e) => {
    e.preventDefault();
    setRevealing(true);
    try {
      const { data } = await api.post(`/chef/banks/${revealId}/reveal`, { password: revealPwd });
      setRevealedData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRevealing(false); }
  };

  const copyIban = () => {
    if (revealedData?.iban) {
      navigator.clipboard.writeText(revealedData.iban);
      toast.success("IBAN copié");
    }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Trésorerie"
        title="Comptes bancaires"
        sub="Gérez les comptes bancaires de l'entreprise. L'IBAN n'est révélé qu'après confirmation de votre mot de passe."
        right={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty()); } }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="bank-add-btn" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Ajouter un compte</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
                  <Landmark className="h-5 w-5 text-[#002FA7]" /> {editing ? "Modifier le compte" : "Nouveau compte bancaire"}
                </DialogTitle>
                <DialogDescription className="text-xs">L'IBAN sera stocké chiffré et masqué par défaut.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="bank-form">
                <F label="Nom de la banque" value={form.nom_banque} onChange={(v) => setForm({ ...form, nom_banque: v })} required testid="bank-nom" />
                <F label="Titulaire du compte" value={form.titulaire} onChange={(v) => setForm({ ...form, titulaire: v })} required testid="bank-titulaire" />
                <F label={editing ? "IBAN (laisser vide pour garder l'actuel)" : "IBAN"} value={form.iban} onChange={(v) => setForm({ ...form, iban: v.toUpperCase() })} required={!editing} testid="bank-iban" placeholder="FR76 ..." />
                <F label="BIC (optionnel)" value={form.bic} onChange={(v) => setForm({ ...form, bic: v.toUpperCase() })} testid="bank-bic" />
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Note (optionnel)</Label>
                  <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="bank-note" />
                </div>
                <DialogFooter><Button type="submit" data-testid="bank-submit" className="bg-[#002FA7] text-white">{editing ? "Enregistrer" : "Ajouter"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6]" data-testid="banks-list">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">
              <Landmark className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
              Aucun compte bancaire. Cliquez sur "Ajouter un compte".
            </div>
          ) : items.map((b) => (
            <div key={b.id} className="p-5 border-b border-[#DEE2E6] last:border-b-0 grid lg:grid-cols-12 gap-4 items-center" data-testid={`bank-${b.id}`}>
              <div className="lg:col-span-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-bold flex items-center gap-1.5"><Landmark className="h-3 w-3" /> {b.nom_banque}</div>
                <div className="font-display font-bold mt-1">{b.titulaire}</div>
              </div>
              <div className="lg:col-span-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold mb-1">IBAN</div>
                <div className="font-mono text-sm font-bold tracking-wider" data-testid={`bank-iban-masked-${b.id}`}>{b.iban_masked || "—"}</div>
                {b.bic && <div className="text-[10px] text-[#868E96] font-mono mt-0.5">BIC : {b.bic}</div>}
              </div>
              <div className="lg:col-span-2 text-xs text-[#495057] italic">{b.note || ""}</div>
              <div className="lg:col-span-2 flex justify-end gap-1.5 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => startReveal(b.id)} data-testid={`bank-reveal-${b.id}`} className="h-8 border-[#FFD700] text-[#0A0A0A] hover:bg-[#FFD700]"><Eye className="h-3 w-3 mr-1" /> IBAN</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`bank-edit-${b.id}`} className="h-8"><Edit3 className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(b.id)} data-testid={`bank-delete-${b.id}`} className="h-8 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reveal dialog */}
      <Dialog open={!!revealId} onOpenChange={(o) => { if (!o) { setRevealId(null); setRevealedData(null); setRevealPwd(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-[#FFD700]" /> Confirmer pour révéler l'IBAN
            </DialogTitle>
            <DialogDescription className="text-xs">Pour des raisons de sécurité, votre mot de passe est requis pour afficher l'IBAN complet.</DialogDescription>
          </DialogHeader>
          {!revealedData ? (
            <form onSubmit={submitReveal} className="space-y-4" data-testid="reveal-form">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5"><KeyRound className="h-3 w-3" /> Votre mot de passe</Label>
                <Input type="password" value={revealPwd} onChange={(e) => setRevealPwd(e.target.value)} required data-testid="reveal-pwd" className="h-11" />
              </div>
              <DialogFooter><Button type="submit" disabled={revealing} data-testid="reveal-submit" className="bg-[#002FA7] text-white">{revealing ? "Vérification…" : "Révéler l'IBAN"}</Button></DialogFooter>
            </form>
          ) : (
            <div className="space-y-3" data-testid="reveal-result">
              <div className="p-4 bg-[#ECFDF5] border border-[#A7F3D0] flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#059669] mt-0.5" />
                <div className="text-sm">Vérification réussie. L'IBAN s'affiche ci-dessous (ne pas partager).</div>
              </div>
              <div className="p-4 border border-[#0A0A0A] bg-[#0A0A0A] text-white font-mono text-sm">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFD700] font-bold mb-2">IBAN</div>
                <div className="text-base font-bold tracking-wider break-all" data-testid="reveal-iban">{revealedData.iban}</div>
                {revealedData.bic && <div className="text-xs mt-2 text-white/70">BIC : {revealedData.bic}</div>}
              </div>
              <div className="flex gap-2">
                <Button onClick={copyIban} variant="outline" className="flex-1" data-testid="reveal-copy"><Copy className="h-4 w-4 mr-2" /> Copier l'IBAN</Button>
                <Button onClick={() => { setRevealId(null); setRevealedData(null); setRevealPwd(""); }} className="flex-1 bg-[#0A0A0A] text-white"><EyeOff className="h-4 w-4 mr-2" /> Masquer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{label}{required && <span className="text-[#E60000]"> *</span>}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className="h-11" />
    </div>
  );
}
